// Supplier.final.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import Select from "react-select";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import Export from "../../../../formHelper/export";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import Success from "../../../../formHelper/success";
import Delete from "../../../../formHelper/delete";
import exportApiCall from "../../../../apiHelper/export";
import FilterDropdown from "../../../../formHelper/filterDropDown";
import Loader from "../../../../formHelper/Loader";
import SupplierTable from "../../../../formHelper/supplierList";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import type { RootState } from "../../../../core/data/redux/store";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useRef } from "react";

const Supplier = () => {
  interface Supplier {
    id: number;
    date: string;
    product_id: string;
    principal_id: string;
    branch_id?: string;
    user_id?: string;
    currency_id?: string;
    source_id?: string;
    rate_fc?: string;
    factor_fc?: string;
    total_cost?: string;
    discount?: string;
    net_price?: string;
    custom_price?: string;
    profit?: string;
    product: { id: number; part_no: string; description: string };
    principal: { id: number; type: string };
    source: { id: number; name: string };
    currency: { id: number; name: string };
    branch: { id: number; name: string };
  }

  interface Branch {
    id: number;
    name: string;
  }

  interface ProductRow {
    [key: string]: any;
  }

  interface Permission {
    id: number;
    role_id: number;
    name: string;
    role: {
      permissions: Record<string, Record<string, string>>;
    };
  }

  interface ProductDropdownItem {
    id: number;
    label: string;
    part_no: string;
    description: string;
    principal?: { id: number; type: string } | null;
  }

  type FormError = {
    product_list?: { [index: number]: { [field: string]: string | undefined } };
  } & { [key: string]: string | undefined };

  type GroupedSupplierData = Record<string, Supplier[]>;

  // --- States ---
  const [list, setList] = useState<GroupedSupplierData>({});
  const [formData, setFormData] = useState<any>({
    date: "",
    product_id: "",
    principal_id: "",
    product_description: "",
    principal_type: "",
  });
  const [productRows, setProductRows] = useState<ProductRow[]>([
    {
      id: "",
      date: "",
      currency_id: "",
      source_id: "",
      rate_fc: "",
      factor_fc: "",
      total_cost: "",
      discount: "",
      net_price: "",
      profit: "",
      custom_price: "",
    },
  ]);

  const location = useLocation();
  const partIdRef = useRef(location.state?.partId);
  const partRef = location.state?.partInfo;

  const [formError, setFormError] = useState<FormError>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [query, setQuery] = useState(partRef ?? "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() =>
    typeof window !== "undefined" && window.innerWidth <= 768 ? 5 : 10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const [productList, setProductList] = useState<ProductDropdownItem[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<number[]>([]);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [principalFilter, setPrincipalFilter] = useState<number[]>([]);
  const [sourceFilter, setSourceFilter] = useState<number[]>([]);
  const [productFilter, setProductFilter] = useState<number[]>([]);
  const [currencyFilter, setCurrencyFilter] = useState<number[]>([]);

  const [currencyList, setCurrencyList] = useState<Branch[]>([]);
  const [sourceList, setSourceList] = useState<Branch[]>([]);
  const [branchList, setBranchList] = useState<Branch[]>([]);
  const [editTriggered, setEditTriggered] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Export states
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const backendRoute = all_routes.backend;
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  // Submit / delete states
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767.98px)").matches
      : false
  );

  // --- Helpers (rounding, parsing) ---
  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  const toNumber = (v: any): number => {
    if (v === null || v === undefined || v === "") return NaN;
    const s = String(v).trim().replace(/,/g, "");
    const parsed = Number(s);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const calculateRow = (row: ProductRow): ProductRow => {
    const r: ProductRow = { ...row };

    const rate = toNumber(r.rate_fc);
    const factor = toNumber(r.factor_fc);

    const total_cost =
      Number.isNaN(rate) || Number.isNaN(factor) ? "" : round2(rate * factor);

    const discountPct = toNumber(r.discount);
    const effectiveDiscountPct = Number.isNaN(discountPct) ? 0 : discountPct;

    let discountAmount = 0;
    if (total_cost !== "") {
      discountAmount = (Number(total_cost) * effectiveDiscountPct) / 100;
    }

    const net_price =
      total_cost === "" ? "" : round2(Number(total_cost) - discountAmount);

    const profitPct = toNumber(r.profit);
    const effectiveProfitPct = Number.isNaN(profitPct) ? 0 : profitPct;

    const custom_price =
      net_price === ""
        ? ""
        : round2(
            Number(net_price) + (Number(net_price) * effectiveProfitPct) / 100
          );

    r.total_cost = total_cost === "" ? "" : String(total_cost);
    r.net_price = net_price === "" ? "" : String(net_price);
    r.custom_price = custom_price === "" ? "" : String(custom_price);

    return r;
  };

  const parseDMYtoYMD = (dateString: string): string => {
    if (!dateString) return "";
    const [day, month, year] = dateString.split("-");
    if (!day || !month || !year) return "";
    return `${year}-${month}-${day}`;
  };

  const toDateInputFormat = (value: string): string => {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // --- Parse errors helper ---
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const parseErrors = (errors: Record<string, string>): FormError => {
    const result: FormError = {};
    for (const key in errors) {
      if (key.startsWith("product_list.")) {
        const parts = key.split(".");
        const index = Number(parts[1]);
        const field = parts[2];
        if (!isNaN(index) && field) {
          result.product_list ??= {};
          result.product_list[index] ??= {};
          const readableField = field.replace(/_/g, " ");
          result.product_list[index][field] = `${capitalize(
            readableField
          )} field is required.`;
        }
      } else {
        result[key] = errors[key];
      }
    }
    return result;
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        product_list: productRows.map((r) => ({ ...r })),
        update_status: !!selectedId,
        supplier_id: selectedId,
      };

      const result = await apiRequestCall(
        "post",
        backendRoute.addUpdateSupplier,
        {
          data: payload,
        }
      );

      if (result?.data?.code === 221) {
        setFormError(parseErrors(result?.data?.errors));
        return;
      }

      if (result?.data?.code === 200) {
        resetForm();
        setShowSuccess(true);
        setShowMessage(result?.data?.message);
        fetchList({ page: currentPage, perPage, search: searchText });
      }
    } catch (err) {
      console.error("Submit failed:", err);
      alert("Request failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Update record
  const updateRecord = (row: any) => {
    if (!row || !row.product?.part_no) return;

    const partNo = row.product.part_no;
    const productDetails = list[partNo] || [];
    const selected = productDetails.find((x) => x.id === row.id);
    if (!selected) return;
    resetForm();
    setFormData({
      ...formData,
      date: selected.date ? toDateInputFormat(selected.date) : "",
      product_id: selected.product_id || "",
      principal_id: selected.principal?.id || "",
      product_description: selected.product?.description || "",
      principal_type: selected.principal?.type || "",
    });

    setProductRows(
      productDetails.length
        ? productDetails.map((item) =>
            calculateRow({
              id: item.id,
              source_id: item.source_id,
              currency_id: item.currency_id || item.currency?.id || "",
              rate_fc: item.rate_fc || "",
              factor_fc: item.factor_fc || "",
              total_cost: item.total_cost || "",
              discount: item.discount || "",
              net_price: item.net_price || "",
              custom_price: item.custom_price || "",
              profit: item.profit || "",
              product_id: item.product_id || item.product?.id || "",
              branch_id: item.branch_id || item.branch?.id || "",
              user_id: item.user_id || "",
              principal_id: item.principal_id || item.principal?.id || "",
              date: item.date || "",
            })
          )
        : [
            calculateRow({
              source_id: "",
              currency_id: "",
              rate_fc: "",
              factor_fc: "",
              total_cost: "",
              discount: "",
              net_price: "",
              custom_price: "",
              profit: "",
              product_id: "",
              branch_id: "",
              user_id: "",
              principal_id: "",
              date: parseDMYtoYMD(formData.date),
            }),
          ]
    );

    setSelectedId(selected.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      date: parseDMYtoYMD(formData.date),
      product_id: "",
      principal_id: "",
      product_description: "",
      principal_type: "",
    });
    setProductRows([
      {
        currency_id: "",
        source_id: "",
        rate_fc: "",
        factor_fc: "",
        total_cost: "",
        discount: "",
        net_price: "",
        profit: "",
        custom_price: "",
      },
    ]);
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
  };

  const deleteRecord = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await apiRequestCall("post", backendRoute.deleteSupplier, {
        data: { id: deleteId },
      });
      if (result?.data?.code === 200) {
        fetchList({ page: currentPage, perPage, search: searchText });
        setShowMessage(result?.data?.message);
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed. Please try again.");
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      setDeleting(false);
    }
  };

  const addNewProduct = () => {
    setProductRows((prev) => [
      ...prev,
      {
        currency_id: "",
        source_id: "",
        rate_fc: "",
        factor_fc: "",
        total_cost: "",
        discount: "",
        net_price: "",
        profit: "",
        custom_price: "",
      },
    ]);
  };

  const deleteProduct = (index: number) => {
    setProductRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: string, value: any) => {
    setProductRows((prev) => {
      const updatedRows = [...prev];
      const row = { ...updatedRows[index], [field]: value };

      const recalculated = calculateRow(row);

      updatedRows[index] = recalculated;
      return updatedRows;
    });

    setFormError((prev) => {
      const updatedErrors = { ...prev };
      if (!updatedErrors.product_list?.[index]) return updatedErrors;
      const newRowErrors = { ...updatedErrors.product_list[index] };
      delete newRowErrors[field];
      if (Object.keys(newRowErrors).length === 0) {
        const newProductList = { ...updatedErrors.product_list };
        delete newProductList[index];
        if (Object.keys(newProductList).length === 0) {
          delete updatedErrors.product_list;
        } else {
          updatedErrors.product_list = newProductList;
        }
      } else {
        updatedErrors.product_list = {
          ...updatedErrors.product_list,
          [index]: newRowErrors,
        };
      }
      return updatedErrors;
    });
  };

  // --- Fetch dropdowns ---
  const getBranch = async () => {
    const result = await apiRequestCall("post", backendRoute.getBranchDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setBranchList(
        Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }))
      );
    }
  };

  const getProduct = async () => {
    const result = await apiRequestCall("post", backendRoute.getProductDD);
    const raw = result?.data?.data;
    if (Array.isArray(raw)) {
      setProductList(
        raw.map((item: any) => ({
          id: item.id,
          label: item.part_no || "",
          part_no: item.part_no || "",
          description: item.description || "",
          principal: item.principal || null,
        }))
      );
    }
  };

  const getSource = async () => {
    const result = await apiRequestCall("post", backendRoute.getSourceDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setSourceList(
        Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }))
      );
    }
  };

  const getCurrency = async () => {
    const result = await apiRequestCall("post", backendRoute.getCurrencyDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setCurrencyList(
        Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }))
      );
    }
  };

  // --- Fetch list ---
  const fetchList = async ({ page = 1, perPage = 10, search = "" }) => {
    setLoading(true);
    if (search) {
      setLoadingSearch(true);
    }
    try {
      const result = await apiRequestCall("post", backendRoute.getSupplier, {
        data: {
          page,
          per_page: perPage,
          start_date: startDate,
          end_date: endDate,
          search: search,
          owner: ownerFilter,
          branch: branchFilter,
          principal: principalFilter,
          source: sourceFilter,
          product: productFilter,
          currency: currencyFilter,
          partId: partIdRef ?? "",
        },
      });

      const res = result?.data?.data;
      if (res) {
        setList(res.data);
        setCurrentPage(res.current_page);
        setPerPage(res.per_page);
        setTotalItems(res.total);
        setLoadingSearch(false);
      } else {
        setLoading(false);
        setLoadingSearch(false);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
      setLoading(false);
      setLoadingSearch(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    partIdRef.current = null;
    setQuery(value);
  };

  // Common: trigger file download
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  // Poll API for local download
  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  // Export function
  const exportFile = async () => {
    try {
      setExporting(true);
      setDownloading(false);

      const result = await apiRequestCall("post", backendRoute.getSupplier, {
        data: {
          start_date: startDate,
          end_date: endDate,
          search: query.trim(),
          owner: ownerFilter,
          branch: branchFilter,
          principal: principalFilter,
          source: sourceFilter,
          product: productFilter,
          currency: currencyFilter,
          download: true,
        },
      });

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        setExporting(false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename);
    } catch (error) {
      console.error("Export request failed:", error);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  };

  // --- Effects ---
  useEffect(() => {
    getCurrency();
    getSource();
    getBranch();
    getProduct();
  }, []);

  useEffect(() => {
    fetchList({ page: currentPage, perPage, search: searchText });
  }, [
    currentPage,
    perPage,
    startDate,
    endDate,
    ownerFilter,
    branchFilter,
    principalFilter,
    sourceFilter,
    productFilter,
    currencyFilter,
    searchText,
  ]);

  /* ================= PERMISSION CORE ================= */

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  const columns = [
    // {
    //   key: "id",
    //   label: "Sr. No",
    //   render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
    // },
    { key: "product.part_no", label: "Part No." },
    { key: "principal.type", label: "Maker" },
    { key: "product.description", label: "Description" },
    { key: "source.name", label: "Source" },
    { key: "currency.name", label: "Currency" },
    { key: "rate_fc", label: "Rate FC" },
    { key: "factor_fc", label: "Factor FC" },
    { key: "total_cost", label: "Total Cost" },
    { key: "discount", label: "Discount (%)" },
    { key: "net_price", label: "Net Price" },
    { key: "profit", label: "Profit (%)" },
    { key: "custom_price", label: "Custom Price" },
    // { key: "branch.name", label: "Branch" },
    {
      key: "date",
      label: "Created At",
      render: (row: any) =>
        row.date ? dayjs(row.date).format("DD/MM/YYYY") : "",
    },
    ...(permissionMap?.supplier?.edit_supplier ||
    permissionMap?.supplier?.delete_supplier
      ? [
          {
            key: "action",
            label: "Action",
            render: (row: any) => (
              <div className="hstack gap-2 fs-15">
                {permissionMap?.supplier?.edit_supplier && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-info"
                    onClick={() => updateRecord(row)}
                    disabled={submitting || deleting}
                    title="Edit"
                    aria-label={`Edit supplier ${
                      row?.product?.part_no || row.id
                    }`}
                  >
                    <i className="feather-edit" />
                  </button>
                )}

                {permissionMap?.supplier?.delete_supplier && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-danger"
                    onClick={() => deleteRecord(Number(row.product_id))}
                    disabled={submitting || deleting}
                    title="Delete"
                    aria-label={`Delete supplier ${
                      row?.product?.part_no || row.id
                    }`}
                  >
                    <i className="feather-trash" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const mapOpts = (arr: { id: any; name: string }[]) =>
    arr.map((b) => ({ value: b.id, label: b.name }));

  useEffect(() => {
    if (showForm) {
      document
        .querySelector(".offcanvas.offcanvas-end")
        ?.setAttribute("style", "width:1400px !important; visibility:visible;");
    }
  }, [showForm]);

  useEffect(() => {
    if (!partIdRef.current || !list || editTriggered) return;
    const flatList = Object.values(list ?? {}).flat();
    const row = flatList.find((r: any) => r?.product_id === partIdRef.current);
    if (row) {
      updateRecord(row);
      setEditTriggered(true);
    }
  }, [partIdRef.current, list, editTriggered]);

  const searchTextData = (text: string) => {
    setSearchText(text);
  };

  useEffect(() => {
    const css = `
  /* --- RoleAccessSingleFile styles --- */
  .ra-container { padding: 1rem; max-height: 100vh; overflow-y: auto; }
  .ra-perm-wrapper { width: 100%; overflow-x: auto; }
  .ra-perm-table { width: 100%; border-collapse: collapse; min-width: 700px; font-size: 14px; }
  .ra-perm-table th, .ra-perm-table td { padding: 10px 8px; border: 1px solid #e8e8e8; text-align: left; vertical-align: middle; }
  .ra-perm-table th { background: #fafafa; font-weight: 600; }
  .col-module { min-width: 200px; font-weight: 700; }
  .perm-list { display:flex; flex-direction:column; gap:6px; }
  .muted { color: var(--ra-muted, #888); }

  /* Mobile cards */
  .ra-perm-cards { display:none; }
  .perm-card { border:1px solid #e6e6e6; border-radius:8px; margin-bottom:12px; overflow:hidden; background:#fff; }
  .perm-card-header { padding:10px; font-weight:700; background:#f7f7f7; }
  .perm-card-body { padding:10px; display:flex; flex-direction:column; gap:8px; }
  .perm-card-row { display:flex; gap:8px; align-items:flex-start; border-top:1px solid #f0f0f0; padding:8px 0; }
  .perm-card-row:first-child{ border-top:none; }
  .perm-card-label { min-width:120px; font-weight:600; }
  .perm-card-values { flex:1; display:flex; flex-direction:column; gap:6px; }

  /* Offcanvas responsive width */
  .custom-offcanvas { transition: width 180ms ease; }
  @media (max-width: 767.98px) {
    .custom-offcanvas { width: 100% !important; right:0; left:0; }
    .offcanvas-backdrop { display:block; } /* ensure backdrop on mobile */
    .ra-perm-table { display:none; }
    .ra-perm-cards { display:block; }
  }
  @media (min-width: 768px) {
    .custom-offcanvas { width: 1000px !important; }
  }
  @media (min-width: 1200px) {
    .custom-offcanvas { width: 1200px !important; }
  }

  /* Accessibility focus */
  .ra-checkbox input[type="checkbox"]:focus + span,
  .ra-checkbox input[type="checkbox"]:focus { outline: 2px solid #3b82f6; outline-offset:2px; }

  /* Small helpers */
  .icon-form .form-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#6b7280; }
  .icon-form input.form-control { padding-left:38px; }
  @media (max-width: 576px) {
    .icon-form .form-icon { left:8px; }
    .icon-form input.form-control { padding-left:36px; }
  }
  `;
    const id = "roleaccess-singlefile-styles";
    if (!document.getElementById(id)) {
      const node = document.createElement("style");
      node.id = id;
      node.innerHTML = css;
      document.head.appendChild(node);
    }
    return () => {
      // keep style alive across component lifecycle — do not remove to avoid FOUC on re-mount
    };
  }, []);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* ---------- Top header (title + actions) ---------- */}
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h5 className="page-title mb-0">
                  Supplier <span className="count-title">{totalItems}</span>
                </h5>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  {/* Export (mobile + desktop handled by Export component / CSS) */}

                  {permissionMap?.["supplier"]?.["export_supplier"] && (
                    <Export
                      options={[
                        {
                          label: downloading
                            ? "Downloading..."
                            : exporting
                            ? "Generating..."
                            : "Export",
                          iconClass: downloading
                            ? "ti ti-download"
                            : exporting
                            ? "ti ti-loader"
                            : "ti ti-file-type-xls",
                          colorClass:
                            exporting || downloading
                              ? "text-muted"
                              : "text-primary",
                          onClick:
                            exporting || downloading ? undefined : exportFile,
                        },
                      ]}
                    />
                  )}

                  {/* New button */}

                  {permissionMap?.["supplier"]?.["add_supplier"] && (
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      disabled={submitting}
                      aria-label="Add new supplier"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* ---------- Filters card (compact, doesn't bloat header) ---------- */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-4">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Enter Text ..."
                    value={query}
                    onChange={setQuery}
                    searchText={searchTextData}
                    loading={loadingSearch}
                  />
                </div>
              </div>
              {/* <div className="col-12 col-md-2">
                  <div className="icon-form">
                    <span className="form-icon">
                      <i className="ti ti-calendar" />
                    </span>
                    <DateRangePicker
                      initialSettings={{
                        autoUpdateInput: false,
                        locale: { cancelLabel: "Clear" },
                      }}
                      onCallback={(start, end) => {
                        setStartDate(start.format("YYYY-MM-DD"));
                        setEndDate(end.format("YYYY-MM-DD"));
                        setCurrentPage(1);
                      }}
                      onCancel={() => {
                        setStartDate("");
                        setEndDate("");
                        setCurrentPage(1);
                      }}
                    >
                      <input
                        type="text"
                        className="form-control bookingrange"
                        placeholder="Select Date Range Filter"
                        value={
                          startDate && endDate
                            ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                                endDate
                              ).format("DD-MM-YYYY")}`
                            : ""
                        }
                        readOnly
                        style={{ cursor: "pointer" }}
                        aria-label="Select date range"
                      />
                    </DateRangePicker>
                  </div>
                </div> */}

              {/* <div className="col-12 col-md-3">
                  <FilterDropdown
                    label="Owner"
                    endpoint="/getOwnerDD"
                    selected={ownerFilter}
                    onChange={(v) => setOwnerFilter(v as number[])}
                  />
                </div> */}
              {/* <div className="col-12 col-md-3">
                  <FilterDropdown
                    label="Branch"
                    endpoint="/getBranchDD"
                    selected={branchFilter}
                    onChange={(v) => setBranchFilter(v as number[])}
                  />
                </div> */}
              <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Principal"
                  endpoint="/getPrincipalDD"
                  selected={principalFilter}
                  onChange={(v) => setPrincipalFilter(v as number[])}
                />
              </div>
              <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Source"
                  endpoint="/getSourceDD"
                  selected={sourceFilter}
                  onChange={(v) => setSourceFilter(v as number[])}
                />
              </div>
              {/* <div className="col-12 col-md-2">
                  <FilterDropdown
                    label="Product"
                    endpoint="/getProductDD"
                    selected={productFilter}
                    onChange={(v) => setProductFilter(v as number[])}
                  />
                </div> */}
              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Currency"
                  endpoint="/getCurrencyDD"
                  selected={currencyFilter}
                  onChange={(v) => setCurrencyFilter(v as number[])}
                />
              </div>
              {/* /.col */}
            </div>{" "}
            {/* /.row */}
          </div>{" "}
          {/* /.card-body */}
        </div>{" "}
        {/* /.card */}
        {/* ---------- Table card + pagination ---------- */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <Loader />
              ) : (
                <SupplierTable data={list} columns={columns} />
              )}
            </div>
          </div>

          {/* pagination sits outside card-body (keeps footer compact) */}
          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p) => {
              setCurrentPage(p);
              fetchList({ page: p, perPage, search: searchText });
            }}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setCurrentPage(1);
              fetchList({ page: 1, perPage: pp, search: searchText });
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>
        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          style={{
            visibility: showForm ? "visible" : "hidden",
          }}
          tabIndex={-1}
          aria-modal={showForm}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom offcanvas-large-custom">
            <h5 className="fw-semibold">
              {selectedId ? "Update Supplier" : "Add Supplier"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setShowForm(false);
                setFormData(formData);
              }}
              disabled={submitting}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <div className="form-desktop">
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Date <span className="text-danger">*</span>
                    </label>
                    <DatePicker
                      value={
                        formData?.date &&
                        dayjs(formData.date, "YYYY-MM-DD", true).isValid()
                          ? dayjs(formData.date, "YYYY-MM-DD")
                          : dayjs()
                      }
                      format="DD-MM-YYYY"
                      onChange={(date) => {
                        setFormData({
                          ...formData,
                          date: date
                            ? date.format("YYYY-MM-DD")
                            : dayjs().format("YYYY-MM-DD"),
                        });

                        setFormError((prev: any) => {
                          const updated = { ...prev };
                          delete updated.date;
                          return updated;
                        });
                      }}
                      className="form-control"
                      disabled={submitting}
                    />
                    {formError?.date && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError?.date}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="col-form-label">
                      Select Part No. <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="product_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={productList}
                      value={
                        productList.find(
                          (opt) => opt.id === formData?.product_id
                        ) || null
                      }
                      onChange={(selected: any) => {
                        setFormError((prev) => {
                          const updated = { ...prev };
                          delete updated.product_id;
                          return updated;
                        });
                        setFormData((prev: any) => ({
                          ...prev,
                          product_id: selected?.id || null,
                          principal_id: selected?.principal?.id || null,
                          product_description: selected?.description || "",
                          principal_type: selected?.principal?.type || "",
                        }));
                      }}
                      isClearable
                      isDisabled={submitting}
                    />
                    {formError.product_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.product_id}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="col-form-label">
                      Principal <span className="text-danger">*</span>
                    </label>
                    <input
                      readOnly
                      name="principal_type"
                      type="text"
                      className="form-control"
                      value={formData.principal_type}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Description <span className="text-danger">*</span>
                    </label>
                    <textarea
                      readOnly
                      name="product_description"
                      className="form-control"
                      style={{ height: "208px" }}
                      value={formData.product_description}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="mb-3">
                  <label className="col-form-label">
                    Rate &amp; FC <span className="text-danger">*</span>
                  </label>

                  {!isMobile ? (
                    <div className="table-responsive">
                      <table className="table table-view">
                        <thead>
                          <tr>
                            {selectedId && (
                              <th style={{ width: "200px" }}>Date</th>
                            )}
                            <th style={{ width: "200px" }}>Source</th>
                            <th style={{ width: "200px" }}>Currency</th>
                            <th style={{ width: "100px" }}>Rate FC</th>
                            <th style={{ width: "100px" }}>Factor FC</th>
                            <th style={{ width: "200px" }}>Total Cost</th>
                            <th style={{ width: "100px" }}>Discount (%)</th>
                            <th style={{ width: "200px" }}>Net Price</th>
                            <th style={{ width: "100px" }}>Profit (%)</th>
                            <th style={{ width: "200px" }}>Custom Price</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productRows.map((row: ProductRow, index: number) => (
                            <tr key={index}>
                              {selectedId && (
                                <td>
                                  <DatePicker
                                    value={
                                      row?.date
                                        ? dayjs(row.date, "YYYY-MM-DD")
                                        : null
                                    }
                                    format="DD-MM-YYYY"
                                    onChange={(date) => {
                                      const formatted = date
                                        ? date.format("YYYY-MM-DD")
                                        : "";

                                      setProductRows((prev) =>
                                        prev.map((r, i) =>
                                          i === index
                                            ? { ...r, date: formatted }
                                            : r
                                        )
                                      );
                                    }}
                                    className="form-control"
                                    disabled={submitting}
                                    allowClear={false}
                                  />
                                </td>
                              )}
                              <td>
                                <Select
                                  classNamePrefix="react-select"
                                  className="tagging"
                                  options={mapOpts(sourceList)}
                                  value={
                                    mapOpts(sourceList).find(
                                      (opt) => opt.value === row.source_id
                                    ) || null
                                  }
                                  onChange={(selected: any) =>
                                    updateRow(
                                      index,
                                      "source_id",
                                      selected?.value ?? null
                                    )
                                  }
                                  isClearable
                                  isDisabled={submitting}
                                  menuPortalTarget={menuPortalTarget}
                                  menuPosition="fixed"
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                      gap: 10,
                                    }),

                                    control: (base, state) => ({
                                      ...base,
                                      borderColor: state.isFocused
                                        ? "#86b7fe"
                                        : "#ced4da",
                                      boxShadow: state.isFocused
                                        ? "0 0 0 0.1rem rgba(13,110,253,.25)"
                                        : "none",
                                      "&:hover": {
                                        borderColor: "#86b7fe",
                                      },
                                    }),
                                  }}
                                />

                                {formError?.product_list?.[index]
                                  ?.source_id && (
                                  <div className="text-danger small">
                                    {formError.product_list[index].source_id}
                                  </div>
                                )}
                              </td>

                              <td>
                                <Select
                                  classNamePrefix="react-select"
                                  className="tagging"
                                  options={mapOpts(currencyList)}
                                  value={
                                    mapOpts(currencyList).find(
                                      (opt) => opt.value === row.currency_id
                                    ) || null
                                  }
                                  onChange={(selected: any) =>
                                    updateRow(
                                      index,
                                      "currency_id",
                                      selected?.value ?? null
                                    )
                                  }
                                  isClearable
                                  isDisabled={submitting}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                                {formError?.product_list?.[index]
                                  ?.currency_id && (
                                  <div className="text-danger small">
                                    {formError.product_list[index].currency_id}
                                  </div>
                                )}
                              </td>

                              {(
                                [
                                  "rate_fc",
                                  "factor_fc",
                                  "total_cost",
                                  "discount",
                                  "net_price",
                                  "profit",
                                  "custom_price",
                                ] as const
                              ).map((field) => (
                                <td key={field}>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={row[field] ?? ""}
                                    onChange={(e) =>
                                      updateRow(index, field, e.target.value)
                                    }
                                    readOnly={[
                                      "total_cost",
                                      "net_price",
                                      "custom_price",
                                    ].includes(field)}
                                    disabled={submitting}
                                  />
                                  {formError?.product_list?.[index]?.[
                                    field
                                  ] && (
                                    <div className="text-danger small">
                                      {formError.product_list[index][field]}
                                    </div>
                                  )}
                                </td>
                              ))}

                              <td>
                                <div className="d-inline-flex gap-2 mb-3">
                                  <Link
                                    to="#"
                                    className="btn btn-icon btn-sm btn-soft-danger rounded-pill"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      deleteProduct(index);
                                    }}
                                    style={{
                                      pointerEvents:
                                        productRows.length === 1
                                          ? "none"
                                          : "auto",
                                      opacity:
                                        productRows.length === 1 ? 0.5 : 1,
                                    }}
                                    aria-disabled={
                                      productRows.length === 1 || submitting
                                    }
                                  >
                                    <i className="feather-trash" />
                                  </Link>

                                  {index === productRows.length - 1 && (
                                    <Link
                                      to="#"
                                      className="btn btn-icon btn-sm btn-soft-info rounded-pill"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        addNewProduct();
                                      }}
                                      aria-disabled={submitting}
                                    >
                                      <i className="feather-plus" />
                                    </Link>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    // MOBILE / CARD view
                    <div className="rf-card-list" role="list">
                      {productRows.map((row: ProductRow, index: number) => {
                        const srcOpts = mapOpts(sourceList);
                        const curOpts = mapOpts(currencyList);
                        const srcSelected =
                          srcOpts.find((o) => o.value === row.source_id) ||
                          null;
                        const curSelected =
                          curOpts.find((o) => o.value === row.currency_id) ||
                          null;

                        return (
                          <div
                            className="rf-card"
                            key={index}
                            role="listitem"
                            aria-label={`product-row-${index}`}
                          >
                            {/* top row: Source & Currency compact */}
                            <div className="rf-row-top">
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: "#374151",
                                      marginBottom: 6,
                                    }}
                                  >
                                    Source
                                  </div>
                                  <Select
                                    classNamePrefix="react-select"
                                    className="tagging"
                                    options={srcOpts}
                                    value={srcSelected}
                                    onChange={(selected: any) =>
                                      updateRow(
                                        index,
                                        "source_id",
                                        selected?.value ?? null
                                      )
                                    }
                                    isClearable
                                    isDisabled={submitting}
                                  />
                                  {formError?.product_list?.[index]
                                    ?.source_id && (
                                    <div className="text-danger small">
                                      {formError.product_list[index].source_id}
                                    </div>
                                  )}
                                </div>

                                <div style={{ width: 12 }} />

                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: "#374151",
                                      marginBottom: 6,
                                    }}
                                  >
                                    Currency
                                  </div>
                                  <Select
                                    classNamePrefix="react-select"
                                    className="tagging"
                                    options={curOpts}
                                    value={curSelected}
                                    onChange={(selected: any) =>
                                      updateRow(
                                        index,
                                        "currency_id",
                                        selected?.value ?? null
                                      )
                                    }
                                    isClearable
                                    isDisabled={submitting}
                                  />
                                  {formError?.product_list?.[index]
                                    ?.currency_id && (
                                    <div className="text-danger small">
                                      {
                                        formError.product_list[index]
                                          .currency_id
                                      }
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* compact action buttons */}
                            </div>

                            {/* stacked numeric fields */}
                            {(
                              [
                                {
                                  key: "rate_fc",
                                  label: "Rate FC",
                                  readOnly: false,
                                },
                                {
                                  key: "factor_fc",
                                  label: "Factor FC",
                                  readOnly: false,
                                },
                                {
                                  key: "total_cost",
                                  label: "Total Cost",
                                  readOnly: true,
                                },
                                {
                                  key: "discount",
                                  label: "Discount (%)",
                                  readOnly: false,
                                },
                                {
                                  key: "net_price",
                                  label: "Net Price",
                                  readOnly: true,
                                },
                                {
                                  key: "profit",
                                  label: "Profit (%)",
                                  readOnly: false,
                                },
                                {
                                  key: "custom_price",
                                  label: "Custom Price",
                                  readOnly: true,
                                },
                              ] as const
                            ).map(({ key, label, readOnly }) => (
                              <div className="rf-card-row" key={String(key)}>
                                <div className="rf-label">{label}</div>
                                <div className="rf-value">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={(row as any)[key] ?? ""}
                                    onChange={(e) =>
                                      updateRow(
                                        index,
                                        String(key),
                                        e.target.value
                                      )
                                    }
                                    readOnly={readOnly}
                                    disabled={submitting}
                                  />
                                  {formError?.product_list?.[index]?.[key] && (
                                    <div className="text-danger small">
                                      {formError.product_list[index][key]}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            <div className="rf-compact-controls">
                              <div className="rf-action-buttons">
                                <Link
                                  to="#"
                                  className="btn btn-icon btn-sm btn-soft-danger rounded-pill"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    deleteProduct(index);
                                  }}
                                  style={{
                                    pointerEvents:
                                      productRows.length === 1
                                        ? "none"
                                        : "auto",
                                    opacity: productRows.length === 1 ? 0.5 : 1,
                                  }}
                                  aria-disabled={
                                    productRows.length === 1 || submitting
                                  }
                                >
                                  <i className="feather-trash" />
                                </Link>

                                {index === productRows.length - 1 && (
                                  <Link
                                    to="#"
                                    className="btn btn-icon btn-sm btn-soft-info rounded-pill"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      addNewProduct();
                                    }}
                                    aria-disabled={submitting}
                                  >
                                    <i className="feather-plus" />
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => {
                    setShowForm(false);
                    setFormData(formData);
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {selectedId ? "Updating..." : "Creating..."}
                    </>
                  ) : selectedId ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </div>

            {/* Mobile stacked form (each field in new row; each product as card) */}
            <div className="form-mobile" style={{ display: "none" }}>
              <div className="mb-3">
                <label className="col-form-label">
                  Date <span className="text-danger">*</span>
                </label>
                <DatePicker
                  value={
                    formData.date ? dayjs(formData.date, "YYYY-MM-DD") : null
                  }
                  format="DD-MM-YYYY"
                  onChange={(date) => {
                    const formatted = date ? date.format("YYYY-MM-DD") : "";
                    setFormData({ ...formData, date: formatted });
                    setFormError((prev: any) => {
                      const updated = { ...prev };
                      delete updated.date;
                      return updated;
                    });
                  }}
                  className="form-control"
                  disabled={submitting}
                />
                {formError?.date && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError?.date}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Select Part No. <span className="text-danger">*</span>
                </label>
                <Select
                  name="product_id"
                  classNamePrefix="react-select"
                  className="tagging"
                  options={productList}
                  value={
                    productList.find(
                      (opt) => opt.id === formData?.product_id
                    ) || null
                  }
                  onChange={(selected: any) => {
                    setFormError((prev) => {
                      const updated = { ...prev };
                      delete updated.product_id;
                      return updated;
                    });
                    setFormData((prev: any) => ({
                      ...prev,
                      product_id: selected?.id || null,
                      principal_id: selected?.principal?.id || null,
                      product_description: selected?.description || "",
                      principal_type: selected?.principal?.type || "",
                    }));
                  }}
                  isClearable
                  isDisabled={submitting}
                />
                {formError.product_id && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.product_id}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Principal <span className="text-danger">*</span>
                </label>
                <input
                  readOnly
                  name="principal_type"
                  type="text"
                  className="form-control"
                  value={formData.principal_type}
                />
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Description <span className="text-danger">*</span>
                </label>
                <textarea
                  readOnly
                  name="product_description"
                  className="form-control"
                  style={{ minHeight: "120px" }}
                  value={formData.product_description}
                />
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Rate & FC <span className="text-danger">*</span>
                </label>

                {/* Each product row as stacked card on mobile */}
                {productRows.map((row, index) => (
                  <div
                    key={index}
                    className="product-mobile-card"
                    aria-label={`Product row ${index + 1}`}
                  >
                    <div className="product-mobile-row">
                      <div className="col">
                        <label className="form-label small">Source</label>
                        <Select
                          classNamePrefix="react-select"
                          className="tagging"
                          options={sourceList.map((b) => ({
                            value: b.id,
                            label: b.name,
                          }))}
                          value={sourceList
                            .map((b) => ({ value: b.id, label: b.name }))
                            .find((opt) => opt.value === row.source_id)}
                          onChange={(selected: any) =>
                            updateRow(
                              index,
                              "source_id",
                              selected?.value || null
                            )
                          }
                          isClearable
                          isDisabled={submitting}
                        />
                        {formError.product_list?.[index]?.source_id && (
                          <div className="text-danger small">
                            {formError.product_list[index].source_id}
                          </div>
                        )}
                      </div>

                      <div className="col">
                        <label className="form-label small">Currency</label>
                        <Select
                          classNamePrefix="react-select"
                          className="tagging"
                          options={currencyList.map((b) => ({
                            value: b.id,
                            label: b.name,
                          }))}
                          value={currencyList
                            .map((b) => ({ value: b.id, label: b.name }))
                            .find((opt) => opt.value === row.currency_id)}
                          onChange={(selected: any) =>
                            updateRow(
                              index,
                              "currency_id",
                              selected?.value || null
                            )
                          }
                          isClearable
                          isDisabled={submitting}
                        />
                        {formError.product_list?.[index]?.currency_id && (
                          <div className="text-danger small">
                            {formError.product_list[index].currency_id}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="product-mobile-row">
                      <div className="col">
                        <label className="form-label small">Rate FC</label>
                        <input
                          type="text"
                          className="form-control"
                          value={row.rate_fc ?? ""}
                          onChange={(e) =>
                            updateRow(index, "rate_fc", e.target.value)
                          }
                          disabled={submitting}
                        />
                        {formError.product_list?.[index]?.rate_fc && (
                          <div className="text-danger small">
                            {formError.product_list[index].rate_fc}
                          </div>
                        )}
                      </div>

                      <div className="col">
                        <label className="form-label small">Factor FC</label>
                        <input
                          type="text"
                          className="form-control"
                          value={row.factor_fc ?? ""}
                          onChange={(e) =>
                            updateRow(index, "factor_fc", e.target.value)
                          }
                          disabled={submitting}
                        />
                        {formError.product_list?.[index]?.factor_fc && (
                          <div className="text-danger small">
                            {formError.product_list[index].factor_fc}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="product-mobile-row">
                      <div className="col">
                        <label className="form-label small">Total Cost</label>
                        <input
                          type="text"
                          className="form-control"
                          value={row.total_cost ?? ""}
                          readOnly
                          disabled={submitting}
                        />
                      </div>

                      <div className="col">
                        <label className="form-label small">Discount (%)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={row.discount ?? ""}
                          onChange={(e) =>
                            updateRow(index, "discount", e.target.value)
                          }
                          disabled={submitting}
                        />
                        {formError.product_list?.[index]?.discount && (
                          <div className="text-danger small">
                            {formError.product_list[index].discount}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="product-mobile-row">
                      <div className="col">
                        <label className="form-label small">Net Price</label>
                        <input
                          type="text"
                          className="form-control"
                          value={row.net_price ?? ""}
                          readOnly
                          disabled={submitting}
                        />
                      </div>

                      <div className="col">
                        <label className="form-label small">Profit (%)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={row.profit ?? ""}
                          onChange={(e) =>
                            updateRow(index, "profit", e.target.value)
                          }
                          disabled={submitting}
                        />
                        {formError.product_list?.[index]?.profit && (
                          <div className="text-danger small">
                            {formError.product_list[index].profit}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="product-mobile-row"
                      style={{ alignItems: "center" }}
                    >
                      <div className="col">
                        <label className="form-label small">Custom Price</label>
                        <input
                          type="text"
                          className="form-control"
                          value={row.custom_price ?? ""}
                          readOnly
                          disabled={submitting}
                        />
                      </div>

                      <div className="col product-mobile-actions">
                        <Link
                          to="#"
                          className="btn btn-icon btn-sm btn-soft-danger"
                          onClick={() => deleteProduct(index)}
                          style={{
                            pointerEvents:
                              productRows.length === 1 ? "none" : "auto",
                            opacity: productRows.length === 1 ? 0.5 : 1,
                          }}
                          aria-disabled={productRows.length === 1 || submitting}
                        >
                          <i className="feather-trash" />
                        </Link>
                        {index === productRows.length - 1 && (
                          <Link
                            to="#"
                            className="btn btn-icon btn-sm btn-soft-info"
                            onClick={addNewProduct}
                            aria-disabled={submitting}
                          >
                            <i className="feather-plus" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {selectedId ? "Updating..." : "Creating..."}
                    </>
                  ) : selectedId ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Success
          id="formatSuccessModal"
          title="Supplier"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />
        <Delete
          show={showDeleteModal}
          title="Delete Supplier?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default Supplier;
